/**
 * Set a report's client price via PATCH /reports/{id}/pricing (admin).
 *
 * The price is saved first and then pushed to the report's Backoffice
 * project; if that push fails the person is told the price saved but
 * Backoffice is behind. A completed report's price needs a `reason` (10+
 * characters), because its figures may already have been billed. A refused
 * change (no reason given, or a price removed once the project exists) is
 * shown as the server's own message and resolves to `null`.
 */

import { useCallback } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { transformToBackofficeSync } from '@/types/backoffice-sync';
import type { ReportResponse } from '@/types/report';
import { notifyIfBackofficeBehind } from '@/utils/backofficeSyncNotice';

export interface UseReportPricingResult {
  updatePrice: (
    reportId: string,
    priceOverride: number | null,
    reason?: string | null
  ) => Promise<ReportResponse | null>;
}

export function useReportPricing(): UseReportPricingResult {
  const updatePrice = useCallback(
    async (reportId: string, priceOverride: number | null, reason?: string | null) => {
      try {
        const updated = await vnextApiClient.patchReportPricing(reportId, priceOverride, reason);
        notifyIfBackofficeBehind(transformToBackofficeSync(updated.backoffice_sync));
        return updated;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        notifications.show({ title: 'Price update failed', message: e.message, color: 'red' });
        return null;
      }
    },
    []
  );

  return { updatePrice };
}
